import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../constants';

interface SearchResult {
  id: string;
  username: string;
  email: string;
  bio?: string;
  avatar?: string;
  role: 'creator' | 'consumer';
  isSubscribed?: boolean;
}

interface SearchBarProps {
  className?: string;
  placeholder?: string;
}

const SearchBar: React.FC<SearchBarProps> = ({
  className = "",
  placeholder = "Search by username, email, or bio..."
}) => {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Real-time search - search after each character with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (query.trim() && query.length >= 1) {
        searchProfiles();
      } else {
        setResults([]);
        setIsOpen(false);
      }
    }, 150); // Fast search on each character

    return () => clearTimeout(timeoutId);
  }, [query]);

  const searchProfiles = async () => {
    if (!query.trim()) return;

    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/users/search?query=${encodeURIComponent(query)}&page=1&limit=8`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('🔍 Search response:', data);
        setResults(data.users || []);
        setIsOpen(true);
      } else {
        const errorData = await response.json();
        console.error('Search failed:', response.status, errorData);
        setResults([]);
      }
    } catch (error) {
      console.error('Error searching profiles:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleResultClick = (userId: string) => {
    navigate(`/profile/${userId}`);
    setQuery('');
    setResults([]);
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const handleViewAllResults = () => {
    navigate(`/profile-search?q=${encodeURIComponent(query)}`);
    setQuery('');
    setResults([]);
    setIsOpen(false);
    inputRef.current?.blur();
  };

  const clearSearch = () => {
    setQuery('');
    setResults([]);
    setIsOpen(false);
    inputRef.current?.focus();
  };

  return (
    <div ref={searchRef} className={`position-relative ${className}`}>
      <div className="position-relative">
        <div className="position-absolute" style={{ left: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
          <i className="feather-search font-xss text-grey-500"></i>
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="form-control font-xsss fw-500 text-grey-700 bg-transparent border-0 ps-5 pe-4"
          style={{
            paddingLeft: '40px',
            paddingRight: query ? '40px' : '16px',
            backgroundColor: '#f8f9fa',
            borderRadius: '25px',
            height: '40px'
          }}
        />
        {query && (
          <button
            onClick={clearSearch}
            className="position-absolute btn p-0 border-0 bg-transparent"
            style={{ right: '12px', top: '50%', transform: 'translateY(-50%)' }}
          >
            <i className="feather-x font-xss text-grey-500"></i>
          </button>
        )}
      </div>

      {/* Search Results Dropdown */}
      {isOpen && (query.length >= 1) && (
        <div
          className="position-absolute w-100 bg-white rounded-3 shadow-lg border-0 mt-2"
          style={{
            zIndex: 1050,
            maxHeight: '400px',
            overflowY: 'auto',
            boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)'
          }}
        >
          {loading ? (
            <div className="p-4 text-center">
              <div className="spinner-border spinner-border-sm text-primary me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <span className="font-xsss text-grey-600">Searching...</span>
            </div>
          ) : results.length > 0 ? (
            <>
              {results.map((profile, index) => (
                <div
                  key={profile.id}
                  onClick={() => handleResultClick(profile.id)}
                  className={`p-3 d-flex align-items-center cursor-pointer ${index < results.length - 1 ? 'border-bottom' : ''}`}
                  style={{
                    borderColor: '#f1f1f1',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <img
                    src={profile.avatar || '/assets/images/user.png'}
                    alt={profile.username}
                    className="rounded-circle me-3"
                    style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                  />
                  <div className="flex-grow-1">
                    <div className="d-flex align-items-center mb-1">
                      <h6 className="fw-600 font-xsss text-grey-900 mb-0 me-2">
                        @{profile.username}
                      </h6>
                      {profile.role === 'creator' && (
                        <span className="badge bg-warning font-xssss">Creator</span>
                      )}
                    </div>
                    {profile.bio && (
                      <p className="font-xssss text-grey-700 mb-1">{profile.bio.length > 50 ? profile.bio.substring(0, 50) + '...' : profile.bio}</p>
                    )}
                    <p className="font-xssss text-grey-500 mb-0">{profile.email}</p>
                  </div>
                  <i className="feather-user font-xss text-grey-400"></i>
                </div>
              ))}
              {results.length >= 8 && (
                <div
                  onClick={handleViewAllResults}
                  className="p-3 text-center cursor-pointer border-top"
                  style={{
                    borderColor: '#f1f1f1',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <span className="font-xsss fw-600 text-primary">
                    View all results for "{query}"
                  </span>
                </div>
              )}
            </>
          ) : (
            <div className="p-4 text-center">
              <i className="feather-search font-lg text-grey-400 mb-2"></i>
              <p className="font-xsss text-grey-500 mb-0">
                No profiles found for "{query}"
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
